import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPasswordDialog = ({
  open,
  onOpenChange,
  title = "Administrator Password Required",
  description = "Please confirm your administrator password to proceed with this operation.",
  actionText = "Confirm & Proceed",
  variant = "default",
  onConfirm
}) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const handleClose = (openState) => {
    if (!openState) {
      setPassword("");
      setShowPassword(false);
    }
    onOpenChange(openState);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Please enter your administrator password");
      return;
    }

    setVerifying(true);
    try {
      await axios.post(`${API}/auth/verify-admin-password`, { password });
      setPassword("");
      setShowPassword(false);
      onOpenChange(false);
      if (onConfirm) {
        await onConfirm();
      }
    } catch (error) {
      const msg = error.response?.data?.detail || "Incorrect administrator password";
      toast.error(msg);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
            <Lock className="h-5 w-5 text-primary" />
            {title}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {description}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label htmlFor="admin-verify-password">Administrator Password</Label>
            <div className="relative">
              <Input
                id="admin-verify-password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter password..."
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                required
                data-testid="input-admin-verify-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <DialogFooter className="mt-6 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
              disabled={verifying}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant={variant}
              disabled={verifying || !password}
              data-testid="submit-admin-password-verify"
            >
              {verifying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {actionText}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminPasswordDialog;
